"use client";

import { motion } from "motion/react";
import { useTranslations } from "next-intl";
import {
	Box,
    Image,
	Stack,
	Typography,
	useMediaQuery,
	useTheme,
} from "@/components/controls/base-controls";
import {
	HEADER_HEIGHT_FULL,
	WEBPAGE_CORE_CONTAINER,
} from "@/core/constants/webpages";
import useResponsiveImage from "@/core/hooks/use-responsive-image";

const ServicesExperience = () => {
    const t = useTranslations();
    const theme = useTheme();

    const isMd = useMediaQuery(theme.breakpoints.up("md"));
    
    const callout = useResponsiveImage(
		{
			src: "https://general-images.navairra.com/dinner_in_woods_960x680.png",
			width: 420,
			height: 900,
		},
		{
			src: "https://general-images.navairra.com/dinner_in_woods_1015x900.png",
			width: 900,
			height: 420,
		},
		{
			src: "https://general-images.navairra.com/dinner_in_woods_1015x900.png",
			width: 480,
			height: 420,
		},
		{
			src: "https://general-images.navairra.com/dinner_in_woods_1015x900.png",
			width: 768,
			height: 680,
		},
		{
			src: "https://general-images.navairra.com/dinner_in_woods_1015x900.png",
			width: 1015,
			height: 900,
		},
	);

    return (
        <Box
            sx={{
                backgroundColor: "var(--mui-palette-darkest)",
                margin: "0 auto",
                width: WEBPAGE_CORE_CONTAINER,
                maxWidth: "100%",
                padding: { xs: "1em 0 0", md: "0 0" },
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "35fr 65fr" },
                gridTemplateAreas: { xs: `"bottom" "top"`, md: `"top bottom"` },
                gap: "1em",
                height: "100%",
                maxHeight: {
                    xs: "100%",
                    md: `calc(100vh - ${HEADER_HEIGHT_FULL})`,
                },
                overflowY: "hidden",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    alignContent: "center",
                    justifyContent: "center",
                    maxHeight: {
                        xs: "50svh",
                        md: `calc(100vh - ${HEADER_HEIGHT_FULL})`,
                    },
                    "@media (orientation: landscape)": {
                        maxHeight: {
                            xs: "120svh",
                            md: `calc(100vh - ${HEADER_HEIGHT_FULL})`,
                        },
                    },
                    overflow: "hidden",
                    gridArea: "top"
                }}
            >
                <Image
                    src={callout.src}
                    alt={t("services.experience.image1")}
                    width={callout.width}
                    height={callout.height}
                    loading={"eager"}
                    style={{
                        width: "100%",
                        height: `calc(100vh - ${HEADER_HEIGHT_FULL})`,
                        objectFit: "cover",
                        objectPosition: isMd ? "center center" : "center center",
                    }}
                />
            </Box>

            <Stack
                component={motion.div} 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}            
                sx={{
                    direction:"column",
                    justifyContent:"center",
                    padding: { xs: "1em", md: "1em 6em" },
                    gridArea: "bottom"
                }}             
            >
                <Stack>
                    <Typography
                        variant="body1"
                        sx={{
                            color: "var(--mui-palette-lightest)",
                            fontWeight: "bold"
                        }}
                    >
                        {t("services.experience.heading1")}
                    </Typography>

                    <Typography variant="body1" component="div"
                        sx={{
                            color: "var(--mui-palette-lightest)",
                            mt: "1em"
                        }}
                    >
                        {t.rich("services.experience.details1", {
                            p: (chunks) => <p style={{ margin: "1em 0 0" }}>{chunks}</p>
                        })}
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            color: "var(--mui-palette-lightest)",
                            fontWeight: "bold",
                            mt: "1.5em"
                        }}
                    >
                        {t("services.experience.heading2")}
                    </Typography>

                    <Typography variant="body1" component="div"
                        sx={{
                            color: "var(--mui-palette-lightest)",
                            mt: "1em"
                        }}
                    >
                        {t.rich("services.experience.details2", {
                            p: (chunks) => <p style={{ margin: "1em 0 0" }}>{chunks}</p>
                        })}
                    </Typography>

                    <Typography
                        variant="body1"
                        sx={{
                            color: "var(--mui-palette-lightest)",
                            fontWeight: "bold",
                            mt: "1.5em"
                        }}
                    >
                        {t("services.experience.heading3")}
                    </Typography>

                    <Typography variant="body1" component="div"
                        sx={{
                            color: "var(--mui-palette-lightest)",
                            mt: "1em"
                        }}
                    >
                        {t.rich("services.experience.details3", {
                            p: (chunks) => <p style={{ margin: "1em 0 0" }}>{chunks}</p>
                        })}
                    </Typography>
                </Stack>
            </Stack>
        </Box>
    );

};

export default ServicesExperience;
